import React, { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddPatientModal({ isOpen, onClose, onPatientAdded }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    fiscal_code: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    // Validazione campi obbligatori
    if (!formData.email || !formData.phone || !formData.date_of_birth) {
      toast.error('Email, telefono e data di nascita sono obbligatori!');
      return;
    }
    if (!formData.first_name || !formData.last_name) {
      toast.error('Nome e cognome sono obbligatori!');
      return;
    }
    
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/admin/patients/create`, {
        ...formData,
        password: 'primevision',
        role: 'patient'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Paziente ${formData.first_name} ${formData.last_name} creato! Password: primevision`);
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        gender: '',
        fiscal_code: ''
      });
      
      // Chiudi modal e notifica parent
      onClose();
      if (onPatientAdded) {
        onPatientAdded(response.data);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione paziente');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      data-testid="add-patient-modal-overlay"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 transition-opacity" 
        onClick={onClose}
        data-testid="add-patient-modal-backdrop"
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto z-[100000]"
        data-testid="add-patient-modal-content"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-[#2A9FCB]">Registra Nuovo Paziente</h3>
            <p className="text-sm text-gray-500">Inserisci i dati del paziente. Nessun OTP automatico.</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="add-patient-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      
        {/* Form */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
                placeholder="Mario"
                data-testid="input-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
                placeholder="Rossi"
                data-testid="input-last-name"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email * (sarà username login)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
              placeholder="mario.rossi@email.com"
              data-testid="input-email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
              placeholder="+39 333 1234567"
              data-testid="input-phone"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita *</label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
              data-testid="input-dob"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Genere</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({...formData, gender: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
                data-testid="select-gender"
              >
                <option value="">Seleziona...</option>
                <option value="M">Maschio</option>
                <option value="F">Femmina</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
              <input
                type="text"
                value={formData.fiscal_code}
                onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:outline-none"
                placeholder="RSSMRA80A01H501A"
                maxLength={16}
                data-testid="input-fiscal-code"
              />
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            <p>📝 La password di default sarà: <strong>primevision</strong></p>
            <p className="text-xs mt-1">Il paziente potrà cambiarla al primo accesso</p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="btn-cancel"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            style={{background: '#2A9FCB'}}
            data-testid="btn-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creazione...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Registra Paziente
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
